import React from 'react';
import {
  Container,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import { FruitType, Item } from './types';
import { CustomerDialog } from './components/CustomerDialog';
import { ItemsDialog } from './components/ItemsDialog';
import { ChangePasswordDialog } from './components/ChangePasswordDialog';
import { DateOnlyPicker } from './components/DateOnlyPicker';
import { api, PurchasePayload } from './api';
import { printReceipt } from './utils/print';
import { PurchaseListPage, PurchaseListPageRef } from './pages/PurchaseListPage';
import { ReportPage } from './pages/ReportPage';
import { CustomerListPage } from './pages/CustomerListPage';
import { StatisticsPage } from './pages/StatisticsPage';
import { LoginPage } from './pages/LoginPage';
import { authStorage } from './auth';

import './App.css';
import { CONFIG } from './config-global';

type PurchaseRow = {
  id: string;
  purchaseId: string;
  customerId: string;
  name: string;
  phone: string;
  address: string;
  cccd: string;
  purchaseNumber?: string;
  customerType: string;
  items: Item[];
  totalAmount: number;
  date: string;
  createdAt: string;
};

const toIsoDate = (value: string) => new Date(value).toISOString().slice(0, 10);

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(authStorage.isAuthenticated());
  const [fruitTypes, setFruitTypes] = React.useState<Record<string, FruitType>>({});
  const [customerDialogOpen, setCustomerDialogOpen] = React.useState(false);
  const [itemsDialogOpen, setItemsDialogOpen] = React.useState(false);
  const [currentCustomerName, setCurrentCustomerName] = React.useState('');
  const [currentCustomerPhone, setCurrentCustomerPhone] = React.useState('');
  const [currentCustomerAddress, setCurrentCustomerAddress] = React.useState('');
  const [currentCustomerCCCD, setCurrentCustomerCCCD] = React.useState('');
  const [currentPurchaseNumber, setCurrentPurchaseNumber] = React.useState<string | undefined>(undefined);
  const [currentCustomerType, setCurrentCustomerType] = React.useState('Retail');
  const [currentCustomerId, setCurrentCustomerId] = React.useState<string | null>(null);
  const [currentPurchaseDate, setCurrentPurchaseDate] = React.useState<string | null>(new Date().toISOString().slice(0, 10));
  const [currentItems, setCurrentItems] = React.useState<Item[]>([]);
  const [editingCustomerId, setEditingCustomerId] = React.useState<string | null>(null);
  const [editingPurchaseId, setEditingPurchaseId] = React.useState<string | null>(null);
  const [customerEditMode, setCustomerEditMode] = React.useState<'new' | 'edit-info'>('new');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showReport, setShowReport] = React.useState(false);
  const [showCustomerList, setShowCustomerList] = React.useState(false);
  const [showStatistics, setShowStatistics] = React.useState(false);
  const [itemsSaveError, setItemsSaveError] = React.useState<string>('');
  const [itemsSaving, setItemsSaving] = React.useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = React.useState(false);
  const purchaseListRef = React.useRef<PurchaseListPageRef>(null);
  const [reloadCustomerList, setReloadCustomerList] = React.useState<(() => void) | null>(null);

  const resetCurrent = () => {
    setCurrentCustomerName('');
    setCurrentCustomerPhone('');
    setCurrentCustomerAddress('');
    setCurrentCustomerCCCD('');
    setCurrentPurchaseNumber(undefined);
    setCurrentCustomerType('Retail');
    setCurrentCustomerId(null);
    setCurrentItems([]);
    setEditingCustomerId(null);
    setEditingPurchaseId(null);
    setCustomerEditMode('new');
    setItemsSaveError('');
  };

  // Lắng nghe phím Enter để mở màn hình thêm khách hàng
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isAuthenticated) {
        if (!customerDialogOpen && !itemsDialogOpen) {
          e.preventDefault();
          handleAddCustomer();
        }
        else if (itemsDialogOpen) {
          const barcodeInput = document.getElementById('barcode-input');
          const priceInput = document.getElementById('price-input');
          const weightInput = document.getElementById('weight-input');
          if (barcodeInput && document.activeElement !== barcodeInput
            && priceInput && document.activeElement !== priceInput
            && weightInput && document.activeElement !== weightInput
          ) {
            barcodeInput.focus();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [customerDialogOpen, itemsDialogOpen, isAuthenticated]);

  const loadFruitData = React.useCallback(async () => {
    setErrorMessage(null);
    try {
      const fruitData = await api.getFruitTypes();
      const fruitMap = fruitData.reduce<Record<string, FruitType>>((acc, f) => {
        acc[f.id] = f;
        return acc;
      }, {});
      setFruitTypes(fruitMap);
    } catch (err: any) {
      setErrorMessage('Lỗi tải dữ liệu');
    }
  }, []);

  React.useEffect(() => {
    if (isAuthenticated) {
      loadFruitData();
    }
  }, [isAuthenticated, loadFruitData]);

  const handleAddCustomer = () => {
    resetCurrent();
    setCustomerDialogOpen(true);
  };

  const handleCustomerNameSubmit = async (id: string | null, name: string, phone: string, address: string, cccd: string, customerType: string, isChanged: boolean) => {
    try {
      const todayIso = new Date().toISOString();

      if (customerEditMode === 'edit-info' && editingCustomerId) {
        // Khi edit từ CustomerListPage, không cần tìm trong customerList
        // Chỉ cần update trực tiếp với thông tin hiện tại
        await api.updateCustomer(editingCustomerId, {
          name,
          phone,
          address,
          cccd,
          customerType,
          createdAt: todayIso, // API sẽ giữ nguyên createdAt gốc
        });

        // Reset chỉ các field thông tin khách hàng, không reset showCustomerList
        setCurrentCustomerName('');
        setCurrentCustomerPhone('');
        setCurrentCustomerAddress('');
        setCurrentCustomerCCCD('');
        setCurrentCustomerType('Retail');
        setCurrentCustomerId(null);
        setEditingCustomerId(null);
        setCustomerEditMode('new');
        setCustomerDialogOpen(false);
        // Load lại danh sách customer nếu đang ở trang customer list
        if (reloadCustomerList) {
          reloadCustomerList();
        }
        return;
      }

      let targetCustomerId = id;

      if (!id) {
        const created = await api.createCustomer({
          name,
          phone,
          address,
          cccd,
          customerType,
          createdAt: todayIso,
        });
        targetCustomerId = created.id;
      } else if (isChanged) {
        await api.updateCustomer(id, {
          name,
          address,
          phone,
          cccd,
          customerType
        });
      }

      setCurrentCustomerName(name);
      setCurrentCustomerPhone(phone);
      setCurrentCustomerAddress(address);
      setCurrentCustomerCCCD(cccd);
      setCurrentCustomerType(customerType);
      setCurrentCustomerId(targetCustomerId);
      setCustomerDialogOpen(false);
      // Load lại danh sách customer nếu đang ở trang customer list
      if (showCustomerList && reloadCustomerList) {
        reloadCustomerList();
      }
      // Chỉ mở ItemsDialog nếu không đang ở trang danh sách khách hàng
      if (!showCustomerList) {
        setItemsDialogOpen(true);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Lỗi xử lý khách hàng');
    }
  };

  const handleItemsSave = async (items: Item[], selectedDate: string): Promise<boolean> => {
    if (!currentCustomerId) {
      setItemsSaveError('Thiếu thông tin khách hàng');
      return false;
    }

    setItemsSaving(true);
    setItemsSaveError('');

    const clientNow = new Date();
    const dateWithClientTime = `${selectedDate}T${clientNow.toTimeString().slice(0, 8)}`;

    const payload: PurchasePayload = {
      customerId: currentCustomerId,
      date: dateWithClientTime,
      notes: '',
      items: items.map((i) => ({
        id: i.id,
        fruitTypeId: i.fruitTypeId,
        fruitTypeName: i.fruitTypeName,
        price: Math.trunc(i.price),
        weight: i.weight,
        total: i.total,
        details: i.details ?? i.weight.toString(),
      })),
    };

    try {
      if (editingPurchaseId) {
        await api.updatePurchase(editingPurchaseId, payload);
      } else {
        const created = await api.createPurchase(payload);
        // Lấy printMode từ localStorage và in trực tiếp
        const savedPrintMode = (localStorage.getItem('printMode') || 'double-a5') as any;
        printReceipt({
          customerName: currentCustomerName,
          customerPhone: currentCustomerPhone,
          customerAddress: currentCustomerAddress,
          items: items,
          totalAmount: items.reduce((sum, item) => sum + item.total, 0),
          date: payload.date,
          purchaseNumber: created.purchaseNumber,
          printMode: savedPrintMode,
        });
      }
      // Only close dialog and reset on success
      setItemsDialogOpen(false);
      resetCurrent();
      // Reload purchase list in PurchaseListPage
      if (purchaseListRef.current) {
        const date = currentPurchaseDate ? toIsoDate(currentPurchaseDate) : toIsoDate(new Date().toISOString());
        await purchaseListRef.current.reloadData(date);
      }
      return true;
    } catch (err: any) {
      setItemsSaveError(err.message || 'Lỗi lưu phiếu mua');
      return false;
    } finally {
      setItemsSaving(false);
    }
  };

  const handleUpsertFruitType = async (fruit: FruitType) => {
    const exists = Boolean(fruitTypes[fruit.id]);
    try {
      const saved = await api.upsertFruitType(fruit, exists);
      setFruitTypes((prev) => ({ ...prev, [fruit.id]: { ...fruit, ...saved } }));
    } catch (err: any) {
      setErrorMessage(err.message || 'Lỗi lưu loại trái cây');
    }
  };

  const handleViewCustomer = (purchase: PurchaseRow) => {
    setEditingPurchaseId(purchase.purchaseId);
    setCurrentCustomerId(purchase.customerId);
    setCurrentCustomerName(purchase.name);
    setCurrentCustomerPhone(purchase.phone);
    setCurrentCustomerAddress(purchase.address);
    setCurrentCustomerCCCD(purchase.cccd || '');
    setCurrentPurchaseNumber(purchase.purchaseNumber);
    setCurrentCustomerType(purchase.customerType || 'Retail');
    setCurrentItems(purchase.items);
    setItemsDialogOpen(true);
  };

  const handleEditCustomerInfo = (purchase: PurchaseRow) => {
    setCustomerEditMode('edit-info');
    setEditingCustomerId(purchase.customerId);
    setCurrentCustomerName(purchase.name);
    setCurrentCustomerPhone(purchase.phone);
    setCurrentCustomerAddress(purchase.address);
    setCurrentCustomerCCCD(purchase.cccd || '');
    setCurrentCustomerType(purchase.customerType || 'Retail');
    setCustomerDialogOpen(true);
  };

  const handleLogin = (token: string, username: string) => {
    authStorage.setToken(token);
    authStorage.setUser(username);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authStorage.clearAuth();
    setIsAuthenticated(false);
    setFruitTypes({});
  };

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLogin} />;
  }

  return (
    <>
      <Container
        id="main-container"
        maxWidth={false}
        sx={{
          p: 0,
          m: 0,
          '@media (min-width: 600px)': {
            paddingLeft: '0px',
            paddingRight: '0px',
          },
        }}
      >
        <Box sx={{ p: 2, '@media print': { display: 'none' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0 }}>
            <Typography variant="h4" component="h1">
              🥭{CONFIG.businessName}🥭
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                👨🏻‍💼 {authStorage.getUser()}
              </Typography>
              <IconButton
                color="primary"
                onClick={() => setChangePasswordDialogOpen(true)}
                size="small"
                title="Đổi mật khẩu"
                sx={{ border: '1px solid', borderColor: 'primary.main' }}
              >
                <span style={{ fontSize: 18 }}>🔑</span>
              </IconButton>
              <IconButton
                color="error"
                onClick={handleLogout}
                size="small"
                title="Đăng xuất"
                sx={{ border: '1px solid', borderColor: 'error.main' }}
              >
                <span style={{ fontSize: 18 }}>🚪</span>
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, mt: 2, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <Box>
                <DateOnlyPicker
                  value={currentPurchaseDate ? toIsoDate(currentPurchaseDate) : toIsoDate(new Date().toISOString())}
                  onChange={(newDate) => {
                    setCurrentPurchaseDate(newDate);
                  }}
                  label="Chọn ngày xem"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Button
                variant={showReport ? 'contained' : 'outlined'}
                color="info"
                startIcon={"📊"}
                onClick={() => {
                  setShowReport(true);
                  setShowCustomerList(false);
                  setShowStatistics(false);
                }}
                size="large"
              >
                Báo Cáo
              </Button>
              <Button
                variant={showStatistics ? 'contained' : 'outlined'}
                color="success"
                startIcon={"📈"}
                onClick={() => {
                  setShowReport(false);
                  setShowCustomerList(false);
                  setShowStatistics(true);
                }}
                size="large"
              >
                Thống Kê
              </Button>
              <Button
                variant={!showReport && !showCustomerList && !showStatistics ? 'contained' : 'outlined'}
                color="primary"
                startIcon={"📋"}
                onClick={() => {
                  setShowReport(false);
                  setShowCustomerList(false);
                  setShowStatistics(false);
                }}
                size="large"
              >
                DS Phiếu
              </Button>
              <Button
                variant={showCustomerList ? 'contained' : 'outlined'}
                color="primary"
                startIcon={"👨‍👨‍👦‍👦"}
                onClick={() => {
                  setShowReport(false);
                  setShowCustomerList(true);
                  setShowStatistics(false);
                }}
                size="large"
              >
                DS Khách Hàng
              </Button>
            </Box>
          </Box>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ mx: 2, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 150px)' }}>
          {showReport ? (
            <ReportPage
              selectedDate={currentPurchaseDate ? toIsoDate(currentPurchaseDate) : toIsoDate(new Date().toISOString())}
              onBack={() => setShowReport(false)}
            />
          ) : showStatistics ? (
            <StatisticsPage
              selectedDate={currentPurchaseDate ? toIsoDate(currentPurchaseDate) : toIsoDate(new Date().toISOString())}
            />
          ) : showCustomerList ? (
            <CustomerListPage
              onEditCustomer={(customer) => {
                setEditingCustomerId(customer.id);
                setCustomerEditMode('edit-info');
                setCurrentCustomerName(customer.name);
                setCurrentCustomerPhone(customer.phone);
                setCurrentCustomerAddress(customer.address);
                setCurrentCustomerCCCD(customer.cccd || '');
                setCurrentCustomerType(customer.customerType || 'Retail');
                setCustomerDialogOpen(true);
              }}
              onCreatePurchase={(customer) => {
                setCurrentCustomerId(customer.id);
                setCurrentCustomerName(customer.name);
                setCurrentCustomerPhone(customer.phone);
                setCurrentCustomerAddress(customer.address);
                setCurrentCustomerCCCD(customer.cccd || '');
                setCurrentPurchaseNumber(undefined);
                setCurrentCustomerType(customer.customerType || 'Retail');
                setCurrentItems([]);
                setItemsDialogOpen(true);
              }}
              setReloadCallback={setReloadCustomerList}
            />
          ) : (
            <PurchaseListPage
              ref={purchaseListRef}
              selectedDate={currentPurchaseDate ? toIsoDate(currentPurchaseDate) : toIsoDate(new Date().toISOString())}
              onAddCustomer={handleAddCustomer}
              onViewCustomer={handleViewCustomer}
              onEditCustomerInfo={handleEditCustomerInfo}
              setErrorMessage={(msg) => setErrorMessage(msg)}
            />
          )}
        </Box>

        <CustomerDialog
          open={customerDialogOpen}
          initialName={currentCustomerName}
          initialPhone={currentCustomerPhone}
          initialAddress={currentCustomerAddress}
          initialCCCD={currentCustomerCCCD}
          initialCustomerType={currentCustomerType}
          onClose={() => {
            setCustomerDialogOpen(false);
            // resetCurrent();
          }}
          onSubmit={handleCustomerNameSubmit}
        />

        <ItemsDialog
          open={itemsDialogOpen}
          customerName={currentCustomerName}
          customerPhone={currentCustomerPhone}
          customerAddress={currentCustomerAddress}
          customerType={currentCustomerType}
          purchaseNumber={currentPurchaseNumber}
          currentDate={currentPurchaseDate ? toIsoDate(currentPurchaseDate) : toIsoDate(new Date().toISOString())}
          fruitTypes={fruitTypes}
          initialItems={currentItems}
          saveError={itemsSaveError}
          saving={itemsSaving}
          onClose={() => {
            setItemsDialogOpen(false);
            setItemsSaveError('');
            resetCurrent();
          }}
          onSave={handleItemsSave}
          onUpsertFruitType={handleUpsertFruitType}
        />

        <ChangePasswordDialog
          open={changePasswordDialogOpen}
          onClose={() => setChangePasswordDialogOpen(false)}
        />
      </Container>
    </>
  );
};

export default App;
